import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import { NewsletterForm } from './NewsletterForm';
import type { BaseComponentProps } from '../../types';

/**
 * Varianti Footer
 */
export type FooterVariant = 'simple' | 'multi-column' | 'centered' | 'newsletter' | 'minimal';

/**
 * Link Footer
 */
export interface FooterLink {
  label: string;
  href: string;
}

/**
 * Colonna Footer
 */
export interface FooterColumn {
  title: string;
  links: FooterLink[];
}

/**
 * Social Link
 */
export interface SocialLink {
  name: string;
  href: string;
  icon: React.ReactNode;
}

export interface FooterProps extends HTMLAttributes<HTMLElement>, BaseComponentProps {
  /**
   * Variante footer
   * @default 'simple'
   */
  variant?: FooterVariant;
  /**
   * Logo
   */
  logo?: React.ReactNode;
  /**
   * Links (per simple variant)
   */
  links?: FooterLink[];
  /**
   * Colonne (per multi-column variant)
   */
  columns?: FooterColumn[];
  /**
   * Social links
   */
  social?: SocialLink[];
  /**
   * Copyright text
   */
  copyright?: string;
  /**
   * Callback newsletter submit
   */
  onNewsletterSubmit?: (email: string) => void;
}

/**
 * Footer Component
 * 
 * Footer con 5 varianti diverse.
 * 
 * @example
 * ```tsx
 * <Footer
 *   variant="simple"
 *   logo={<Logo />}
 *   links={[{ label: "Home", href: "/" }]}
 *   copyright="© 2025 Company"
 * />
 * ```
 */
export const Footer = forwardRef<HTMLElement, FooterProps>(
  (
    {
      className,
      variant = 'simple',
      logo,
      links = [],
      columns = [],
      social = [],
      copyright,
      onNewsletterSubmit,
      ...props
    },
    ref
  ) => {
    // Variant: Simple
    if (variant === 'simple') {
      return (
        <footer
          ref={ref}
          className={cn('w-full bg-base-200 border-t border-base-300', className)}
          {...props}
        >
          <div className="container mx-auto px-4 py-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              {/* Logo */}
              <div className="text-xl font-bold">{logo}</div>

              {/* Links */}
              <nav className="flex flex-wrap gap-6 justify-center">
                {links.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className="text-base-content/70 hover:text-base-content transition-colors"
                  >
                    {link.label}
                  </a>
                ))}
              </nav>

              {/* Social */}
              {social.length > 0 && (
                <div className="flex gap-4">
                  {social.map((s) => (
                    <a
                      key={s.name}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base-content/70 hover:text-primary transition-colors"
                      aria-label={s.name}
                    >
                      {s.icon}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {copyright && (
              <div className="text-center mt-8 pt-8 border-t border-base-300">
                <p className="text-base-content/60">{copyright}</p>
              </div>
            )}
          </div>
        </footer>
      );
    }

    // Variant: Multi-Column
    if (variant === 'multi-column') {
      return (
        <footer
          ref={ref}
          className={cn('w-full bg-base-200 border-t border-base-300', className)}
          {...props}
        >
          <div className="container mx-auto px-4 py-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              {/* Logo Column */}
              <div>
                <div className="text-xl font-bold mb-4">{logo}</div>
                <p className="text-base-content/70 text-sm">
                  La tua descrizione o tagline qui.
                </p>
              </div>

              {/* Columns */}
              {columns.map((col) => (
                <div key={col.title}>
                  <h3 className="font-bold text-base-content mb-4">{col.title}</h3>
                  <nav className="space-y-2">
                    {col.links.map((link) => (
                      <a
                        key={link.label}
                        href={link.href}
                        className="block text-base-content/70 hover:text-base-content transition-colors text-sm"
                      >
                        {link.label}
                      </a>
                    ))}
                  </nav>
                </div>
              ))}
            </div>

            {/* Bottom */}
            <div className="flex flex-col md:flex-row items-center justify-between pt-8 border-t border-base-300 gap-4">
              {copyright && <p className="text-base-content/60 text-sm">{copyright}</p>}
              {social.length > 0 && (
                <div className="flex gap-4">
                  {social.map((s) => (
                    <a
                      key={s.name}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base-content/70 hover:text-primary transition-colors"
                      aria-label={s.name}
                    >
                      {s.icon}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </footer>
      );
    }

    // Variant: Centered
    if (variant === 'centered') {
      return (
        <footer
          ref={ref}
          className={cn('w-full bg-base-200 border-t border-base-300', className)}
          {...props}
        >
          <div className="container mx-auto px-4 py-16">
            <div className="text-center space-y-8">
              {/* Logo */}
              <div className="text-2xl font-bold">{logo}</div>

              {/* Links */}
              <nav className="flex flex-wrap gap-6 justify-center">
                {links.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className="text-base-content/70 hover:text-base-content transition-colors"
                  >
                    {link.label}
                  </a>
                ))}
              </nav>

              {/* Social */}
              {social.length > 0 && (
                <div className="flex gap-6 justify-center">
                  {social.map((s) => (
                    <a
                      key={s.name}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base-content/70 hover:text-primary transition-colors text-2xl"
                      aria-label={s.name}
                    >
                      {s.icon}
                    </a>
                  ))}
                </div>
              )}

              {/* Copyright */}
              {copyright && (
                <p className="text-base-content/60 text-sm pt-8 border-t border-base-300">
                  {copyright}
                </p>
              )}
            </div>
          </div>
        </footer>
      );
    }

    // Variant: Newsletter
    if (variant === 'newsletter') {
      return (
        <footer
          ref={ref}
          className={cn('w-full bg-base-200 border-t border-base-300', className)}
          {...props}
        >
          <div className="container mx-auto px-4 py-16">
            <div className="max-w-2xl mx-auto text-center mb-12">
              <h2 className="text-3xl font-bold text-base-content mb-4">
                Resta Aggiornato
              </h2>
              <p className="text-base-content/70 mb-8">
                Iscriviti alla nostra newsletter per ricevere le ultime novità.
              </p>
              <NewsletterForm onSubmit={onNewsletterSubmit} />
            </div>

            <div className="flex flex-col md:flex-row items-center justify-between pt-8 border-t border-base-300 gap-4">
              <div className="text-xl font-bold">{logo}</div>
              
              {social.length > 0 && (
                <div className="flex gap-4">
                  {social.map((s) => (
                    <a
                      key={s.name}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base-content/70 hover:text-primary transition-colors"
                      aria-label={s.name}
                    >
                      {s.icon}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {copyright && (
              <p className="text-center text-base-content/60 text-sm mt-8">
                {copyright}
              </p>
            )}
          </div>
        </footer>
      );
    }

    // Variant: Minimal
    if (variant === 'minimal') {
      return (
        <footer
          ref={ref}
          className={cn('w-full bg-base-100 border-t border-base-300', className)}
          {...props}
        >
          <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-base-content/60">
              {copyright && <p>{copyright}</p>}
              
              {social.length > 0 && (
                <div className="flex gap-4">
                  {social.map((s) => (
                    <a
                      key={s.name}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-primary transition-colors"
                      aria-label={s.name}
                    >
                      {s.icon}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </footer>
      );
    }

    return null;
  }
);

Footer.displayName = 'Footer';

